import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]
  static values = {
    lat: { type: Number, default: 51.5074 },     // Default to London
    lng: { type: Number, default: -0.1278 },     // Default to London
    zoom: { type: Number, default: 17 },
    areas: Array  // Array of site_map_areas with geojson and metadata
  }

  connect() {
    console.log('Map preview controller connected')
    console.log('Map data:', { lat: this.latValue, lng: this.lngValue, zoom: this.zoomValue })
    console.log('Areas to render:', this.areasValue?.length || 0)

    // Initialize tracking arrays for map overlays
    this.overlays = []
    this.googleMapsReady = false

    // ✅ CRITICAL FIX: Listen for Google Maps ready event
    if (typeof google !== 'undefined' && google.maps) {
      this.googleMapsReady = true
    } else {
      console.log('Map preview controller listening for Google Maps API...')
      document.addEventListener('google:maps:ready', (event) => {
        console.log('🎉 Map preview received google:maps:ready event:', event.detail)
        this.googleMapsReady = true

        // Handle pending reload if any
        if (this.pendingReload) {
          console.log('🔄 Executing pending reload after Google Maps loaded')
          this.executeReload(this.pendingReload)
          this.pendingReload = null
        }
        // If we have areas to render and they were waiting, render them now
        else if (this.areasValue?.length > 0 && this.map) {
          console.log('📍 Google Maps now ready, rendering pending areas...')
          this.renderAreas()
        }
      }, { once: true })

      // ✅ CRITICAL FIX: Listen for Google Maps error events
      document.addEventListener('google:maps:error', (event) => {
        console.error('🚨 Map preview received google:maps:error event:', event.detail)
        this.handleMapError(event.detail)
      }, { once: true })
    }

    // Listen for reload and clear events from maps panel
    this.element.addEventListener('map:reload', this.handleReload.bind(this))
    this.element.addEventListener('map:clear', this.handleClear.bind(this))

    // Don't initialize our own map - wait for the map-picker to set it up
    // We'll get access to the map through the shared element
    console.log('Map preview controller waiting for map-picker to initialize map...')
  }

  waitForGoogleMaps() {
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max

    const checkGoogle = () => {
      attempts++
      if (typeof google !== 'undefined' && google.maps) {
        console.log('Google Maps API loaded after', attempts, 'attempts')
        this.initMap()
      } else if (attempts < maxAttempts) {
        setTimeout(checkGoogle, 100)
      } else {
        console.error('Google Maps API failed to load after 5 seconds')
        this.showMapError()
      }
    }
    checkGoogle()
  }

  // Promise-based version for async/await usage
  waitForGoogleMapsAsync() {
    return new Promise((resolve, reject) => {
      let attempts = 0
      const maxAttempts = 50 // 5 seconds max

      const checkGoogle = () => {
        attempts++
        if (typeof google !== 'undefined' && google.maps) {
          console.log('Google Maps API loaded after', attempts, 'attempts (async)')
          resolve()
        } else if (attempts < maxAttempts) {
          setTimeout(checkGoogle, 100)
        } else {
          console.error('Google Maps API failed to load after 5 seconds (async)')
          reject(new Error('Google Maps API failed to load'))
        }
      }
      checkGoogle()
    })
  }

  initMap() {
    try {
      console.log('Initializing preview map...')

      // Map options - satellite view, minimal controls, read-only feel
      const mapOptions = {
        center: { lat: this.latValue, lng: this.lngValue },
        zoom: this.zoomValue,
        mapTypeId: 'satellite',
        disableDefaultUI: true,
        gestureHandling: 'cooperative',  // Read-only feel - requires Ctrl+scroll for zoom
        draggable: true,
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true
      }

      this.map = new google.maps.Map(this.containerTarget, mapOptions)
      console.log('Preview map created successfully')

      // Render all areas/markers after map loads
      this.map.addListener('idle', async () => {
        await this.renderAreas()
      })

    } catch (error) {
      console.error('Error initializing preview map:', error)
      this.showMapError()
    }
  }

  async renderAreas() {
    if (!this.areasValue || this.areasValue.length === 0) {
      console.log('No areas to render')
      return
    }

    // ✅ CRITICAL FIX: Check event-driven readiness instead of polling
    if (!this.googleMapsReady || typeof google === 'undefined' || !google.maps) {
      console.log('Google Maps not ready yet, areas will render when API loads')
      return // The event listener in connect() will call renderAreas() when ready
    }

    // Initialize bounds tracking for auto-zoom (like mtech.sitemaps)
    this.bounds = new google.maps.LatLngBounds()
    this.hasValidBounds = false

    console.log(`Rendering ${this.areasValue.length} areas...`)

    // Debug: Count text label areas (reduced logging)
    const textLabelAreas = this.areasValue.filter(area =>
      area.metadata?.contentType === 'text' ||
      area.geojson?.properties?.svgContent?.includes('<text')
    )
    if (textLabelAreas.length > 0) {
      console.log(`🔤 Found ${textLabelAreas.length} text label areas`)
    }

    // Render areas in parallel for better performance
    const renderPromises = this.areasValue.map(async (area, index) => {
      try {
        await this.renderArea(area)
      } catch (error) {
        console.warn(`Error rendering area ${index}:`, error, area)
      }
    })

    await Promise.all(renderPromises)
    console.log('All areas rendered successfully')

    // Auto-zoom to fit all areas (like mtech.sitemaps)
    this.fitMapToBounds()
  }

  async renderArea(area) {
    const { area_type, geojson, metadata } = area

    if (!geojson || !geojson.geometry) {
      console.warn('❌ Area missing geojson geometry:', area)
      return
    }

    // ✅ PERFORMANCE FIX: Reduced per-area logging to prevent 2000+ console messages
    switch (area_type) {
      case 'point':
        this.renderMarker(geojson.geometry, metadata)
        break
      case 'svg':
        await this.renderSvgOverlay(geojson, metadata)
        break
      case 'polygon':
        this.renderPolygon(geojson, metadata)
        break
      case 'circle':
        this.renderCircle(geojson.geometry, metadata)
        break
      case 'line':
      case 'polyline':
        this.renderLine(geojson, metadata)
        break
      default:
        console.warn(`❓ Unknown area type: ${area_type}`)
    }
  }

  renderMarker(geometry, metadata) {
    let coordinates

    // Handle different geometry types for markers
    if (geometry.type === 'Point') {
      coordinates = geometry.coordinates  // [lng, lat]
    } else if (geometry.type === 'Polygon' && geometry.coordinates && geometry.coordinates[0]) {
      // For SVG areas, use center point or first coordinate
      if (metadata && metadata.centerPoint) {
        coordinates = metadata.centerPoint  // [lng, lat]
      } else {
        coordinates = geometry.coordinates[0][0]  // First coordinate of polygon
      }
    } else {
      console.warn('Invalid geometry for marker:', geometry)
      return
    }

    const [lng, lat] = coordinates  // GeoJSON is [lng, lat]
    const position = { lat, lng }

    // Extend bounds for auto-zoom (like mtech.sitemaps)
    if (this.bounds) {
      this.bounds.extend(new google.maps.LatLng(lat, lng))
      this.hasValidBounds = true
    }

    // Create marker with fallback icon strategy (similar to map_picker_controller)
    const markerOptions = {
      position: position,
      map: this.map
    }

    // Add label if available
    if (metadata?.name || metadata?.label) {
      markerOptions.title = metadata.name || metadata.label
    }

    // Create icon with fallback strategy
    try {
      if (this.canUseSymbolPath()) {
        markerOptions.icon = {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: metadata?.fillColor || this.getColorForType(metadata?.type) || '#FF6B47',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        }
      }
      // ✅ PERFORMANCE FIX: Removed per-marker logging
    } catch (error) {
      console.warn('Error setting marker icon, using default:', error)
    }

    const marker = new google.maps.Marker(markerOptions)

    // Track overlay for cleanup
    if (this.overlays) {
      this.overlays.push(marker)
    }

    return marker
  }

  renderPolygon(geojson, metadata) {
    // Extract geometry and properties from geojson
    const geometry = geojson.geometry
    const properties = geojson.properties

    if (geometry.type !== 'Polygon' || !geometry.coordinates || !geometry.coordinates[0]) {
      console.warn('Invalid polygon geometry:', geometry)
      return
    }

    // GeoJSON polygon coordinates are [[[lng, lat], [lng, lat], ...]]
    const paths = geometry.coordinates[0].map(coord => {
      const latLng = new google.maps.LatLng(coord[1], coord[0])

      // Extend bounds for auto-zoom (like mtech.sitemaps)
      if (this.bounds) {
        this.bounds.extend(latLng)
        this.hasValidBounds = true
      }

      return { lat: coord[1], lng: coord[0] }
    })

    // Define brand color constant to match mtech.sitemaps
    const DEFAULT_POLYGON_COLOR = '#FF4081'  // GoodEvent coral/brand color

    // Read colors from geojson.properties first (correct priority)
    const fillColor = properties?.fillColor ||
                      metadata?.fillColor ||
                      this.getColorForType(metadata?.type) ||
                      DEFAULT_POLYGON_COLOR

    const strokeColor = properties?.strokeColor ||
                        metadata?.strokeColor ||
                        properties?.fillColor ||
                        metadata?.fillColor ||
                        DEFAULT_POLYGON_COLOR

    const fillOpacity = properties?.fillOpacity ||
                        this.extractNumeric(metadata?.fillOpacity) ||
                        0.4  // Slightly higher than before for better visibility

    const strokeOpacity = properties?.strokeOpacity ||
                          this.extractNumeric(metadata?.strokeOpacity) ||
                          1

    const strokeWeight = properties?.strokeWeight ||
                         this.extractNumeric(metadata?.strokeWeight) ||
                         2

    // ✅ PERFORMANCE FIX: Removed per-polygon color logging

    const polygon = new google.maps.Polygon({
      paths,
      map: this.map,
      fillColor: fillColor,
      fillOpacity: fillOpacity,
      strokeColor: strokeColor,
      strokeWeight: strokeWeight,
      strokeOpacity: strokeOpacity
    })

    // Track overlay for cleanup
    if (this.overlays) {
      this.overlays.push(polygon)
    }

    return polygon
  }

  renderCircle(geometry, metadata) {
    const [lng, lat] = geometry.coordinates
    const centerPoint = new google.maps.LatLng(lat, lng)

    // Extend bounds for auto-zoom (like mtech.sitemaps)
    if (this.bounds) {
      this.bounds.extend(centerPoint)
      this.hasValidBounds = true
    }

    const circle = new google.maps.Circle({
      center: { lat, lng },
      radius: this.extractNumeric(metadata?.radius) || 10,
      map: this.map,
      fillColor: metadata?.fillColor || this.getColorForType(metadata?.type) || '#FF6B47',
      fillOpacity: this.extractNumeric(metadata?.fillOpacity) || 0.3,
      strokeColor: metadata?.strokeColor || metadata?.fillColor || '#FF6B47',
      strokeWeight: this.extractNumeric(metadata?.strokeWeight) || 2,
      strokeOpacity: this.extractNumeric(metadata?.strokeOpacity) || 1
    })

    // Track overlay for cleanup
    if (this.overlays) {
      this.overlays.push(circle)
    }

    return circle
  }

  renderLine(geojson, metadata) {
    // Extract geometry and properties from geojson
    const geometry = geojson.geometry
    const properties = geojson.properties

    if (geometry.type !== 'LineString' || !geometry.coordinates) {
      console.warn('Invalid line geometry:', geometry)
      return
    }

    const path = geometry.coordinates.map(coord => {
      const latLng = new google.maps.LatLng(coord[1], coord[0])

      // Extend bounds for auto-zoom (like mtech.sitemaps)
      if (this.bounds) {
        this.bounds.extend(latLng)
        this.hasValidBounds = true
      }

      return { lat: coord[1], lng: coord[0] }
    })

    // Read colors from geojson.properties first (like mtech.sitemaps)
    const strokeColor = properties?.strokeColor ||
                        metadata?.strokeColor ||
                        this.getColorForType(metadata?.type) ||
                        '#FF6B47'

    const strokeWeight = properties?.strokeWeight ||
                         this.extractNumeric(metadata?.strokeWeight) ||
                         3

    const strokeOpacity = properties?.strokeOpacity ||
                          this.extractNumeric(metadata?.strokeOpacity) ||
                          1

    // Check if line should have arrows
    const hasArrows = properties?.strokeStyle === 'arrows' ||
                      (properties?.icons && properties.icons.length > 0)

    let icons = null
    if (hasArrows && properties?.icons) {
      // Use arrow configuration from mtech.sitemaps
      icons = properties.icons.map(iconConfig => ({
        icon: {
          path: iconConfig.icon.path || google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: iconConfig.icon.scale || 5,
          fillColor: iconConfig.icon.fillColor || strokeColor,
          fillOpacity: iconConfig.icon.fillOpacity || 1,
          strokeColor: iconConfig.icon.strokeColor || strokeColor,
          strokeWeight: iconConfig.icon.strokeWeight || 2,
          strokeOpacity: iconConfig.icon.strokeOpacity || 1
        },
        offset: iconConfig.offset || '0',
        repeat: iconConfig.repeat || '24px'
      }))

      // ✅ PERFORMANCE FIX: Removed per-line logging
    }

    const polylineOptions = {
      path,
      map: this.map,
      strokeColor: strokeColor,
      strokeWeight: strokeWeight,
      strokeOpacity: strokeOpacity
    }

    // Add arrows if configured
    if (icons) {
      polylineOptions.icons = icons
    }

    const polyline = new google.maps.Polyline(polylineOptions)

    // Track overlay for cleanup
    if (this.overlays) {
      this.overlays.push(polyline)
    }

    return polyline
  }

  // Render SVG overlay on the map
  async renderSvgOverlay(geojson, metadata) {
    // ✅ PERFORMANCE FIX: Reduced SVG overlay logging to prevent console spam

    // Check for embedded SVG content (text labels) vs external SVG by ID
    const embeddedSvgContent = geojson?.properties?.svgContent
    const svgId = metadata?.svgId

    if (embeddedSvgContent) {
      // Use embedded SVG content directly (for text labels)
      await this.renderEmbeddedSvgOverlay(geojson, metadata, embeddedSvgContent)
      return
    }

    if (!svgId) {
      console.warn('❌ SVG area missing both embedded svgContent and svgId in metadata:', metadata)
      // Fallback to rendering as marker
      this.renderMarker(geojson.geometry, metadata)
      return
    }

    try {
      // Fetch SVG content from the API
      const svgData = await this.fetchSvgContent(svgId)

      if (!svgData || !svgData.svg_content) {
        console.warn(`❌ No SVG content found for ID ${svgId}`)
        // Fallback to rendering as marker
        this.renderMarker(geojson.geometry, metadata)
        return
      }

      // Extract geometry and properties from geojson
      const geometry = geojson.geometry
      const properties = geojson.properties

      // Calculate center point from polygon geometry
      const centerPoint = this.calculatePolygonCenter(geometry)

      if (!centerPoint) {
        console.warn('❌ Could not calculate center point for SVG overlay')
        this.renderMarker(geojson.geometry, metadata)
        return
      }

      // Extend bounds for auto-zoom (like mtech.sitemaps)
      if (this.bounds && centerPoint) {
        this.bounds.extend(new google.maps.LatLng(centerPoint.lat, centerPoint.lng))
        this.hasValidBounds = true
      }

      // Create custom overlay with SVG content - pass both geometry and properties
      const overlay = this.createSvgMapOverlay(centerPoint, svgData, metadata, { geometry, properties })
      // ✅ PERFORMANCE FIX: Removed per-SVG creation logging

    } catch (error) {
      console.error(`❌ Error rendering SVG overlay for ID ${svgId}:`, error)
      // Fallback to rendering as marker
      this.renderMarker(geojson.geometry, metadata)
    }
  }

  // Render SVG overlay with embedded content (for text labels)
  async renderEmbeddedSvgOverlay(geojson, metadata, embeddedSvgContent) {
    // ✅ PERFORMANCE FIX: Reduced embedded SVG logging to prevent console spam

    // Extract geometry and properties from geojson
    const geometry = geojson.geometry
    const properties = geojson.properties

    // Calculate center point from polygon geometry
    const centerPoint = this.calculatePolygonCenter(geometry)

    if (!centerPoint) {
      console.warn('❌ Could not calculate center point for embedded SVG overlay')
      this.renderMarker(geojson.geometry, metadata)
      return
    }

    // Create mock svgData object compatible with existing overlay system
    const svgData = {
      name: metadata?.name || 'Text Label',
      svg_content: embeddedSvgContent,
      default_width: properties?.width || 10,
      default_height: properties?.height || 10
    }

    // Extend bounds for auto-zoom (like mtech.sitemaps)
    if (this.bounds && centerPoint) {
      this.bounds.extend(new google.maps.LatLng(centerPoint.lat, centerPoint.lng))
      this.hasValidBounds = true
    }

    // ✅ PERFORMANCE FIX: Reduced per-SVG overlay creation logging

    // Create custom overlay with embedded SVG content
    const overlay = this.createSvgMapOverlay(centerPoint, svgData, metadata, { geometry, properties })
  }

  // Fetch SVG content from the API
  async fetchSvgContent(svgId) {
    // ✅ PERFORMANCE FIX: Reduced SVG fetch logging to prevent console spam
    try {
      const response = await fetch(`/svg_content/${svgId}`)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      // ✅ PERFORMANCE FIX: Removed per-SVG fetch success logging

      return data
    } catch (error) {
      console.error(`❌ Failed to fetch SVG content for ID ${svgId}:`, error)
      return null
    }
  }

  // Calculate center point of polygon for SVG placement
  calculatePolygonCenter(geometry) {
    if (geometry.type !== 'Polygon' || !geometry.coordinates || !geometry.coordinates[0]) {
      return null
    }

    const coordinates = geometry.coordinates[0]
    let totalLat = 0
    let totalLng = 0
    let count = coordinates.length - 1 // Exclude closing coordinate

    for (let i = 0; i < count; i++) {
      const [lng, lat] = coordinates[i]
      totalLng += lng
      totalLat += lat
    }

    return {
      lat: totalLat / count,
      lng: totalLng / count
    }
  }

  // Create custom Google Maps overlay with SVG content
  createSvgMapOverlay(centerPoint, svgData, metadata, geojsonData) {
    // Create custom overlay class
    class SvgOverlay extends google.maps.OverlayView {
      constructor(position, svgContent, svgData, metadata, geojsonData) {
        super()
        this.position = position
        this.svgContent = svgContent
        this.svgData = svgData
        this.metadata = metadata
        this.geojsonData = geojsonData
        this.div = null

        // Use width/height from GeoJSON properties first, then fall back to SVG template defaults
        this.actualWidth = geojsonData?.properties?.width || svgData.default_width || 10
        this.actualHeight = geojsonData?.properties?.height || svgData.default_height || 10

        // Extract rotation from GeoJSON properties (like mtech.sitemaps)
        this.rotation = geojsonData?.properties?.rotation || metadata?.rotation || 0

        // ✅ PERFORMANCE FIX: Removed per-SVG overlay creation logging
      }

      onAdd() {
        // ✅ PERFORMANCE FIX: Reduced SVG overlay onAdd logging to prevent console spam
        try {
          // Create container div (similar to mtech.sitemaps approach)
          this.div = document.createElement('div')
          this.div.style.position = 'absolute'
          this.div.style.cursor = 'default'
          this.div.style.overflow = 'visible'
          this.div.style.zIndex = 1
          this.div.className = 'svg-overlay'

          // Create an inner container for the SVG content
          const svgContainer = document.createElement('div')
          svgContainer.className = 'svg-container'
          svgContainer.style.width = '100%'
          svgContainer.style.height = '100%'
          svgContainer.style.position = 'relative'
          svgContainer.style.overflow = 'visible'

          // Add the SVG content
          svgContainer.innerHTML = this.svgContent

          // Make sure the SVG scales with the container (same as mtech.sitemaps)
          const svgElement = svgContainer.querySelector('svg')
          if (svgElement) {
            // Set attributes
            svgElement.setAttribute('width', '100%')
            svgElement.setAttribute('height', '100%')

            // Set styles
            svgElement.style.width = '100%'
            svgElement.style.height = '100%'
            svgElement.style.display = 'block'
            svgElement.style.overflow = 'visible'

            // Remove aspect ratio preservation
            svgElement.removeAttribute('preserveAspectRatio')
            // Explicitly set to none to ensure distortion is allowed
            svgElement.setAttribute('preserveAspectRatio', 'none')

            // Add title for accessibility
            if (this.metadata.name) {
              const titleElement = document.createElement('title')
              titleElement.textContent = this.metadata.name
              svgElement.insertBefore(titleElement, svgElement.firstChild)
            }
          } else {
            console.error('❌ No SVG element found in content!')
          }

          // Add the SVG container to the div
          this.div.appendChild(svgContainer)

          // Add to map
          const panes = this.getPanes()
          if (panes && panes.overlayMouseTarget) {
            panes.overlayMouseTarget.appendChild(this.div)
          } else {
            console.error('❌ No panes available for SVG overlay')
            return
          }

          // Initial draw call
          this.draw()

        } catch (error) {
          console.error('❌ Error in SVG overlay onAdd():', error)
        }
      }

      draw() {
        const projection = this.getProjection()
        if (!projection) {
          console.error('No projection available for SVG overlay')
          return
        }

        // Calculate the center point in pixels
        const centerPixel = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(this.position.lat, this.position.lng)
        )

        // Calculate the size in pixels based on meters (same logic as mtech.sitemaps)
        const metersPerDegreeLat = 111320 // approximate meters per degree of latitude
        const metersPerDegreeLng = 111320 * Math.cos(this.position.lat * Math.PI / 180)

        // Convert width/height from meters to degrees - use actual dimensions, not template defaults
        const latOffset = (this.actualHeight / 2) / metersPerDegreeLat
        const lngOffset = (this.actualWidth / 2) / metersPerDegreeLng

        // Calculate the pixel size
        const northEast = new google.maps.LatLng(
          this.position.lat + latOffset,
          this.position.lng + lngOffset
        )
        const southWest = new google.maps.LatLng(
          this.position.lat - latOffset,
          this.position.lng - lngOffset
        )

        const nePixel = projection.fromLatLngToDivPixel(northEast)
        const swPixel = projection.fromLatLngToDivPixel(southWest)

        // Calculate width and height in pixels
        const width = Math.abs(nePixel.x - swPixel.x)
        const height = Math.abs(nePixel.y - swPixel.y)

        if (this.div) {
          // Position the div centered on the position
          this.div.style.left = (centerPixel.x - width / 2) + 'px'
          this.div.style.top = (centerPixel.y - height / 2) + 'px'
          this.div.style.width = width + 'px'
          this.div.style.height = height + 'px'

          // Make sure the div is visible
          this.div.style.display = 'block'

          // Apply rotation (like mtech.sitemaps dual rotation system)
          if (this.rotation && this.rotation !== 0) {
            this.div.style.transform = `rotate(${this.rotation}deg)`
            this.div.style.transformOrigin = 'center center'
          }

          // ✅ PERFORMANCE FIX: Removed per-draw detailed logging to prevent console spam
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div)
          this.div = null
        }
      }
    }

    // Create and add the overlay
    const overlay = new SvgOverlay(centerPoint, svgData.svg_content, svgData, metadata, geojsonData)
    overlay.setMap(this.map)

    // Track overlay for cleanup
    if (this.overlays) {
      this.overlays.push(overlay)
    }

    return overlay
  }

  // Helper methods
  canUseSymbolPath() {
    return google.maps.SymbolPath && google.maps.SymbolPath.CIRCLE
  }

  getColorForType(type) {
    // Assign colors based on area type for better visual distinction
    const typeColors = {
      'Stage': '#FF6B47',
      'Tent': '#4CAF50',
      'Food': '#FFA726',
      'Bar': '#42A5F5',
      'Toilet': '#AB47BC',
      'Security': '#EF5350',
      'Entry': '#66BB6A',
      'Parking': '#78909C'
    }

    return typeColors[type] || '#FF6B47'
  }

  extractNumeric(value) {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      const numeric = parseFloat(value)
      return isNaN(numeric) ? null : numeric
    }
    return null
  }

  // Auto-zoom to fit all areas (like mtech.sitemaps bounds fitting)
  fitMapToBounds() {
    if (this.hasValidBounds && this.map && this.bounds) {
      console.log('📍 Fitting map to bounds of all areas...')

      this.map.fitBounds(this.bounds)

      // Constrain minimum zoom level (don't zoom out beyond level 17)
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() < 17) {
          console.log('📏 Constraining zoom to minimum level 17')
          this.map.setZoom(17)
        }
      })

      console.log('✅ Map fitted to bounds successfully')
    } else {
      console.log('📍 No valid bounds to fit - using default center/zoom')
    }
  }

  // ✅ CRITICAL FIX: Handle error events with user feedback
  handleMapError(errorDetail) {
    const { error, message, canRetry } = errorDetail

    if (error === 'authentication') {
      this.showMapError('Maps authentication failed. Please contact support.', false)
    } else if (error === 'timeout') {
      this.showMapError('Maps loading slowly. Check your connection.', canRetry)
    } else {
      this.showMapError('Map preview temporarily unavailable.', canRetry)
    }
  }

  showMapError(message = 'Map preview temporarily unavailable', showRetry = false) {
    console.error('Map preview error:', message)
    if (this.containerTarget) {
      this.containerTarget.innerHTML = `
        <div class="ge-map-preview__error" style="padding: 20px; text-align: center; color: #666;">
          <div style="margin-bottom: 15px;">
            <i style="font-size: 48px; color: #dc3545;">⚠️</i>
          </div>
          <p style="margin-bottom: 10px;">${message}</p>
          ${showRetry ? '<button class="btn btn-sm btn-primary" onclick="window.location.reload()">Retry</button>' : ''}
        </div>
      `
    }
  }

  disconnect() {
    console.log('Map preview controller disconnected')
    // Remove event listeners
    this.element.removeEventListener('map:reload', this.handleReload.bind(this))
    this.element.removeEventListener('map:clear', this.handleClear.bind(this))
    // Cleanup will be handled by Google Maps automatically
  }

  // Handle reload event from maps panel
  async handleReload(event) {
    console.log('🔄 Map preview: handling reload event', event.detail)

    // ✅ CRITICAL FIX: Check event-driven readiness instead of polling
    if (!this.googleMapsReady || typeof google === 'undefined' || !google.maps) {
      console.log('Google Maps not ready for reload, will reload when API loads')
      // Store reload event to execute when Google Maps is ready
      this.pendingReload = event
      return
    }

    // Execute the actual reload
    this.executeReload(event)
  }

  // Execute the reload logic (separated for reuse)
  async executeReload(event) {
    // ✅ PERFORMANCE FIX: Reduced reload step logging to prevent console spam

    // Get the map instance from the map-picker controller
    this.map = this.getMapFromMapPicker()

    if (!this.map) {
      console.warn('❌ Map not available from map-picker yet, cannot reload')
      return
    }

    // ✅ PERFORMANCE FIX: Removed detailed step-by-step reload logging

    // Clear existing overlays
    this.clearMapOverlays()

    // Re-center and zoom map
    this.map.setCenter({ lat: this.latValue, lng: this.lngValue })
    this.map.setZoom(this.zoomValue)

    // Re-render areas with new data
    await this.renderAreas()

    // ✅ PERFORMANCE FIX: Reduced success logging to single message
    console.log('✅ Map preview reloaded successfully')
  }

  // Get the map instance from the map element
  getMapFromMapPicker() {
    const mapElement = this.containerTarget
    // ✅ PERFORMANCE FIX: Removed per-map lookup logging to prevent console spam
    return mapElement.googleMap || null
  }

  // Handle clear event from maps panel
  handleClear(event) {
    console.log('Map preview: handling clear event')

    if (!this.map) {
      return
    }

    // Clear all overlays but keep map
    this.clearMapOverlays()
  }

  // Clear all map overlays (markers, polygons, etc.)
  clearMapOverlays() {
    if (this.overlays && this.overlays.length > 0) {
      // ✅ PERFORMANCE FIX: Reduced per-clear logging to prevent console spam
      this.overlays.forEach(overlay => {
        if (overlay && overlay.setMap) {
          overlay.setMap(null)
        }
      })
      this.overlays = []
    }
  }

}